import { Button } from "@mantine/core";
import type React from "react";
import type { ClientTrainingRequest } from "../../../types/portal";
import { formatCurrency } from "../../../utils/formatting";

interface ClientRequestsTabProps {
  activeTrainingRequests: ClientTrainingRequest[];
  loading: boolean;
  error: string | null;
  refreshSuccess: boolean;
  onRefresh: () => void;
  onOpenCouponModal: (request: ClientTrainingRequest) => void;
  onJoinSession: (requestId: string) => void;
  onCancelRequest: (request: ClientTrainingRequest) => void;
}

const STATUS_LABELS: Record<string, string> = {
  pending: "Waiting for Trainer",
  awaiting_payment: "Awaiting Your Payment",
  accepted: "Ready to Join",
  active: "In Progress",
  completed: "Completed",
};

const STATUS_CLASSES: Record<string, string> = {
  pending: "text-warning",
  awaiting_payment: "text-warning",
  accepted: "text-info",
  active: "text-success",
  completed: "text-success",
  cancelled: "text-destructive",
};

const ClientRequestsTab: React.FC<ClientRequestsTabProps> = ({
  activeTrainingRequests,
  loading,
  error,
  refreshSuccess,
  onRefresh,
  onOpenCouponModal,
  onJoinSession,
  onCancelRequest,
}) => (
  <div className="bg-card rounded-xl p-4 sm:p-6 shadow-lg border border-border min-h-[400px]">
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
      <h2 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
        Active Training Requests
      </h2>
      <Button
        onClick={onRefresh}
        disabled={loading}
        className={`${
          refreshSuccess
            ? "bg-success text-white"
            : loading
              ? "bg-border cursor-wait"
              : "bg-muted border-border text-muted-foreground"
        }`}
        variant="default"
      >
        {loading ? "Refreshing..." : refreshSuccess ? "Refreshed!" : "Refresh"}
      </Button>
    </div>
    {loading ? (
      <div className="text-center text-[0.9375rem] text-muted-foreground py-12">
        Loading training requests...
      </div>
    ) : error ? (
      <div className="text-center text-[0.9375rem] text-destructive p-6 bg-destructive/10 border border-destructive/25 rounded-lg">
        {error}
      </div>
    ) : activeTrainingRequests.length === 0 ? (
      <div className="text-center py-[60px] px-5 text-muted-foreground">
        <p>No active training requests</p>
        <p>Go to "Active Trainers" to start a training session!</p>
      </div>
    ) : (
      <div className="flex flex-col gap-4 max-h-[600px] overflow-y-auto px-1">
        {activeTrainingRequests.map((request) => {
          const label = STATUS_LABELS[request.status] || request.status;
          const statusClass = STATUS_CLASSES[request.status] || "text-muted-foreground";
          return (
            <div
              key={request.id}
              className="bg-card rounded-xl p-5 border border-border shadow-sm transition-all duration-300"
            >
              <div className="flex flex-col sm:flex-row justify-between items-start mb-4 gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[1.1rem] sm:text-[1.2rem] font-semibold text-foreground mb-1">
                    {request.trainer_name}
                  </div>
                  <div className="text-[0.9rem] text-muted-foreground">
                    Status: <span className={`font-semibold ${statusClass}`}>{label}</span>
                  </div>
                </div>
                <div className="text-[0.85rem] text-muted-foreground text-right">
                  {request.created_at ? new Date(request.created_at).toLocaleString() : ""}
                </div>
              </div>

              <div className="mb-4 p-3 bg-muted rounded-lg">
                <div className="text-[0.9rem] text-foreground mb-1.5">
                  <strong>Session ID:</strong> {request.id}
                </div>
                {request.duration_minutes && (
                  <div className="text-[0.9rem] text-foreground mb-1.5">
                    <strong>Duration:</strong> {request.duration_minutes} minutes
                  </div>
                )}
                <div className="text-[0.9rem] text-foreground mb-1.5">
                  <strong>Trainer Rate:</strong> $
                  {request.first_10_min_rate?.toFixed
                    ? request.first_10_min_rate.toFixed(2)
                    : Number(request.first_10_min_rate || 0).toFixed(2)}{" "}
                  first 10 min + ${Number(request.per_minute_rate || 0).toFixed(2)}/min after
                </div>
                {request.total_price !== null && request.total_price !== undefined && (
                  <div className="text-[0.9rem] text-foreground mb-1.5">
                    <strong>Estimated Session Cost:</strong> {formatCurrency(request.total_price)}
                  </div>
                )}
                {request.notes && (
                  <div className="text-[0.9rem] text-foreground mb-1.5">
                    <strong>Notes:</strong> {request.notes}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-3 justify-end">
                {(request.status === "awaiting_payment" ||
                  (request.status === "pending" && request.payment_status !== "completed")) &&
                  request.payment_status !== "completed" && (
                    <Button
                      color="green"
                      radius="md"
                      onClick={() => onOpenCouponModal(request)}
                      disabled={loading || request.payment_status === "completed"}
                    >
                      Pay {formatCurrency(request.total_price || 0)}
                    </Button>
                  )}
                {request.payment_status === "completed" && (
                  <Button variant="default" className="opacity-70 cursor-not-allowed" disabled>
                    Payment Complete
                  </Button>
                )}
                {request.payment_status === "completed" ? (
                  <Button
                    radius="md"
                    onClick={() => onJoinSession(request.id)}
                    disabled={loading}
                  >
                    Join Session Now
                  </Button>
                ) : request.status === "accepted" || request.status === "active" ? (
                  <Button
                    radius="md"
                    onClick={() => onJoinSession(request.id)}
                    disabled={loading}
                  >
                    Join Session
                  </Button>
                ) : request.status === "pending" && request.payment_status !== "completed" ? (
                  <Button color="yellow" className="opacity-90 cursor-not-allowed" disabled>
                    Waiting for Trainer to Accept
                  </Button>
                ) : request.status !== "awaiting_payment" && request.status !== "pending" ? (
                  <Button variant="default" className="cursor-not-allowed" disabled>
                    Unavailable
                  </Button>
                ) : null}
                <Button
                  color="red"
                  radius="md"
                  onClick={() => onCancelRequest(request)}
                  disabled={loading}
                >
                  Cancel Request
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

export default ClientRequestsTab;
